import { useEffect } from 'react'
import { BarChart2, BookOpen, Calendar, Clapperboard, PackageCheck, Palette, Settings, ShoppingBag, Users, UsersRound } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import AppearanceAdminModule from '../components/AppearanceAdminModule'
import EventsAdminModule from '../components/EventsAdminModule'
import MembersAdminModule from '../components/MembersAdminModule'
import PageHeader from '../components/PageHeader'
import PostsAdminModule from '../components/PostsAdminModule'
import StatisticsModule from '../components/StatisticsModule'
import TechniquesAdminModule from '../components/TechniquesAdminModule'
import ShopProductsAdminModule from '../components/ShopProductsAdminModule'
import ShopOrdersAdminModule from '../components/ShopOrdersAdminModule'
import SettingsAdminModule from '../components/SettingsAdminModule'
import BoardAdminModule from '../components/BoardAdminModule'
import ShowcaseAdminModule from '../components/ShowcaseAdminModule'
import { usePendingOrdersCount } from '../hooks/usePendingOrdersCount'

type AdminSection = 'members' | 'board' | 'showcase' | 'posts' | 'stats' | 'events' | 'techniques' | 'shop-products' | 'shop-orders' | 'appearance' | 'settings'

const sections: Array<{ id: AdminSection; label: string; description: string; icon: typeof Users }> = [
  { id: 'members', label: 'Membres', description: 'Comptes, saisons et grades', icon: Users },
  { id: 'board', label: 'Bureau', description: 'Équipe dirigeante publique', icon: UsersRound },
  { id: 'showcase', label: 'Présentation', description: 'Page publique et galerie', icon: Clapperboard },
  { id: 'posts', label: 'Publications', description: 'Actualités du club', icon: BookOpen },
  { id: 'events', label: 'Événements', description: 'Agenda et inscriptions', icon: Calendar },
  { id: 'techniques', label: 'Techniques', description: 'Bibliothèque pédagogique', icon: BookOpen },
  { id: 'shop-products', label: 'Produits', description: 'Catalogue et stocks', icon: ShoppingBag },
  { id: 'shop-orders', label: 'Commandes', description: 'Paiements et retraits', icon: PackageCheck },
  { id: 'stats', label: 'Statistiques', description: 'Activité et fréquentation', icon: BarChart2 },
  { id: 'appearance', label: 'Apparence', description: 'Images et ambiance', icon: Palette },
  { id: 'settings', label: 'Réglages', description: 'Notifications automatiques', icon: Settings },
]

const validSections = new Set<AdminSection>(sections.map((section) => section.id))

const AdminDashboard = () => {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedSection = searchParams.get('section') as AdminSection | null
  const activeSection: AdminSection = requestedSection && validSections.has(requestedSection) ? requestedSection : 'members'
  const activeMetadata = sections.find((section) => section.id === activeSection) || sections[0]
  const ActiveIcon = activeMetadata.icon
  const pendingOrders = usePendingOrdersCount(Boolean(user?.isAdmin), activeSection)

  useEffect(() => {
    if (requestedSection !== activeSection) setSearchParams({ section: activeSection }, { replace: true })
  }, [activeSection, requestedSection, setSearchParams])

  const renderSection = () => {
    switch (activeSection) {
      case 'posts': return <PostsAdminModule />
      case 'board': return <BoardAdminModule />
      case 'showcase': return <ShowcaseAdminModule />
      case 'events': return <EventsAdminModule />
      case 'techniques': return <TechniquesAdminModule />
      case 'stats': return <StatisticsModule />
      case 'shop-products': return <ShopProductsAdminModule />
      case 'shop-orders': return <ShopOrdersAdminModule />
      case 'appearance': return <AppearanceAdminModule />
      case 'settings': return <SettingsAdminModule />
      default: return <MembersAdminModule />
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Pilotage du club" title="Administration" description={`Bienvenue ${user?.firstName ?? ''}. Retrouvez tous les outils de gestion du dojo dans un espace unifié.`} icon={<BarChart2 />} />
      <div className="admin-layout">
        <aside className="admin-rail" aria-label="Navigation de l’administration">
          <div className="admin-rail-heading"><p className="eyebrow !mb-1">Outils</p><strong>Gestion du club</strong></div>
          <nav className="admin-rail-nav">
            {sections.map(({ id, label, description, icon: Icon }) => (
              <button key={id} type="button" className={`admin-nav-item ${activeSection === id ? 'admin-nav-item-active' : ''}`} onClick={() => setSearchParams({ section: id })} aria-current={activeSection === id ? 'page' : undefined}>
                <span className="admin-nav-icon" aria-hidden="true"><Icon /></span>
                <span><strong>{label}</strong><small>{description}</small></span>
                {id === 'shop-orders' && pendingOrders > 0 && <span className="notification-badge ml-auto">{pendingOrders > 99 ? '99+' : pendingOrders}</span>}
              </button>
            ))}
          </nav>
        </aside>
        <section className="admin-workspace" aria-labelledby="admin-section-title">
          <header className="admin-workspace-header"><div><p className="eyebrow !mb-1">{activeMetadata.description}</p><h2 id="admin-section-title">{activeMetadata.label}</h2></div><span className="admin-workspace-icon" aria-hidden="true"><ActiveIcon /></span></header>
          <div className="admin-workspace-body">{renderSection()}</div>
        </section>
      </div>
    </div>
  )
}

export default AdminDashboard
